import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import {
  Container,
  Card,
  Row as BootstrapRow,
  Col as BootstrapCol,
  Button,
  Alert,
  Modal,
} from "react-bootstrap";
import { Table, Select, Input, Form } from "antd";
import { FilterOutlined, LoadingOutlined } from "@ant-design/icons";
import type { ColumnType } from "antd/es/table/interface";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import toast from "react-hot-toast";

import PageTitle from "@/common/PageTitle/PageTitle";
import FormItem from "@/common/FormItem/FormItem";
import DateRangePicker from "@/common/DateRangePicker/DateRangePicker";
import PaginationRow from "@/components/PaginationRow/PaginationRow";
import { getAppStage } from "@/lib/appStage";
import {
  useCanViewPage,
  useCanEditPage,
  useCanPerformLifecycleDelete,
} from "@/contexts/SuperAdminIdentityContext";
import type { AppDispatch } from "@/app/store";
import {
  processingJobsApi,
  useListProcessingJobsQuery,
  useCountProcessingJobsQuery,
  useBulkArchiveProcessingJobsMutation,
  useBulkRestoreProcessingJobsMutation,
  useBulkDeleteProcessingJobsMutation,
  useCreateProcessingBulkOpMutation,
  useApplyProcessingBulkOpMutation,
  isProcessingApiNotConfigured,
  patchProcessingJobsCounts,
  patchProcessingJobsLists,
  scheduleProcessingJobsListReconcile,
  extractProcessingErrorMessage,
} from "@/features/processing/processingApi";
import { formatSongProcessingLoadFailure } from "@/features/processing/processingLoadError";
import {
  PROCESSING_RECONCILE_DELAY_MS,
  resolveBulkOpSucceededJobIds,
  type ProcessingLifecycleCountOp,
} from "@/features/processing/lifecycleCountPatch";
import {
  floorLifecycleTabCount,
  shouldRefetchLifecycleTabCount,
} from "@/features/processing/lifecycleTabCount";
import {
  ARCHIVABLE_STATES,
  CANONICAL_CHECKPOINT_KEYS,
  CHECKPOINT_LABELS,
  MAX_BULK_LIFECYCLE,
  NORMAL_DELETE_MAX,
  REPROCESSABLE_STATES,
  type BulkReprocessResponse,
  type CountProcessingJobsArgs,
  type CreateProcessingBulkOpRequest,
  type DestructiveDeleteDryRunSummary,
  type LifecycleActionResult,
  type ListProcessingJobsArgs,
  type ProcessingBulkOpStatus,
  type ProcessingDecision,
  type ProcessingJobRow,
  type ProcessingJobState,
} from "@/features/processing/processingTypes";
import {
  ArchiveJobsModal,
  BulkLifecycleResultsModal,
  BulkReprocessResultsModal,
  DeleteJobsModal,
  RestoreJobsModal,
  buildLifecycleCompletionMessage,
  type ElevatedDeleteConfirm,
} from "@/features/processing/lifecycleModals";
import {
  waitForActiveBulkOpTerminal,
} from "@/features/processing/ActiveBulkOpProgressHost";
import { startActiveBulkOp } from "@/features/processing/activeBulkOpSession";
import type { BulkRunProgress } from "@/features/processing/lifecycleModals";
import {
  renderJobStatusBadge,
  renderSongTypeBadge,
  formatDateTime,
  isJobStalled,
} from "@/features/processing/processingDisplay";
import {
  isGovernedJob,
  livePlaybackDisplay,
} from "@/features/processing/playbackIntegrityDisplay";
import { PROCESSING_API_UNAVAILABLE_MESSAGE } from "@/features/processing/processingMessages";
import { useUploaderDirectory } from "@/features/processing/uploaderDisplay";
import { buildJobRefs } from "@/features/processing/jobRef";
import { JobRef } from "@/features/processing/JobRefBadge";
import { archiveBulkOpSelection } from "@/features/processing/archiveBulkOpSelection";
import { formatEntityRef, normalizeAnyRefSearchTerm } from "@/lib/entityRef";
import rowActions from "@/styles/rowActions.module.scss";
import SongProcessingLifecycleTabs, {
  type SongProcessingLifecycleView,
} from "./SongProcessingLifecycleTabs";
import styles from "./processing-jobs.module.scss";

const PAGE_ID = "uploader";
// PJL-3.2 — owner-requested 100-row pages.
const PAGE_SIZE = 100;
// Delay before reconciling with the stream-materialized projection.
// Optimistic count + list hide cover the gap; generation-guarded reconcile
// rejects stale Active/Archived totals (Song Library pattern).
const RECONCILE_DELAY_MS = PROCESSING_RECONCILE_DELAY_MS;

const STATE_OPTIONS: { label: string; value: ProcessingJobState }[] = [
  { label: "Upload Initiated", value: "upload_initiated" },
  { label: "Upload Received", value: "upload_received" },
  { label: "Awaiting Processing", value: "awaiting_processing" },
  { label: "Upload Expired", value: "upload_expired" },
  { label: "Validating", value: "validating" },
  { label: "Scanning", value: "scanning" },
  { label: "Enriching", value: "enriching" },
  { label: "Awaiting Review", value: "awaiting_review" },
  { label: "Linking", value: "linking" },
  { label: "Transcoding", value: "transcoding" },
  { label: "Finalizing", value: "finalizing" },
  { label: "Approved", value: "approved" },
  { label: "Not Approved", value: "not_approved" },
  { label: "Failed", value: "failed" },
];

const DECISION_OPTIONS: { label: string; value: ProcessingDecision }[] = [
  { label: "Approved", value: "approved" },
  { label: "Not Approved", value: "not_approved" },
  { label: "Failed", value: "failed" },
];

const FAILED_CHECKPOINT_OPTIONS = CANONICAL_CHECKPOINT_KEYS.map((key) => ({
  label: CHECKPOINT_LABELS[key] ?? key,
  value: key,
}));

interface FilterState {
  state?: ProcessingJobState;
  decision?: ProcessingDecision;
  failedCheckpoint?: string;
  needsReview: boolean;
  uploaderSub?: string;
  lineageTrackId?: string;
  fromDate?: string;
  toDate?: string;
  /** PJL-3.1 — free-text search, scoped to the current tab (owner UX). */
  search?: string;
}

// Server contract: search terms shorter than this are rejected (API 400), so
// the UI treats 0–1 chars as "clear the search" instead of sending them.
const SEARCH_MIN_CHARS = 2;

// U1b — one-click quick filters mapped onto the existing (mutually-exclusive)
// server-side primary filters. Selecting a chip replaces the primary filter;
// the date range is preserved.
type QuickFilterId = "all" | "needsReview" | "approved" | "notApproved" | "failed";

const QUICK_FILTERS: { id: QuickFilterId; label: string }[] = [
  { id: "all", label: "All" },
  { id: "needsReview", label: "Needs Review" },
  { id: "approved", label: "Approved" },
  { id: "notApproved", label: "Not Approved" },
  { id: "failed", label: "Failed" },
];

function quickFilterFromState(filters: FilterState): QuickFilterId | null {
  const primaries = [
    filters.state,
    filters.decision,
    filters.failedCheckpoint,
    filters.needsReview || undefined,
    filters.uploaderSub,
  ].filter((v) => v !== undefined);
  if (primaries.length === 0) return "all";
  if (primaries.length > 1) return null;
  if (filters.needsReview) return "needsReview";
  if (filters.decision === "approved") return "approved";
  if (filters.decision === "not_approved") return "notApproved";
  if (filters.decision === "failed") return "failed";
  return null;
}

const EMPTY_FILTERS: FilterState = {
  state: undefined,
  decision: undefined,
  failedCheckpoint: undefined,
  needsReview: false,
  uploaderSub: undefined,
  lineageTrackId: undefined,
  fromDate: undefined,
  toDate: undefined,
  search: undefined,
};

type JobsView = SongProcessingLifecycleView;

const ProcessingJobs: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  // Read gate — Processing surfaces require uploader.view (spec §10).
  const canView = useCanViewPage(PAGE_ID);
  // Write gate — bulk reprocess/archive/restore require uploader edit (D3).
  const canEdit = useCanEditPage(PAGE_ID);
  // Hard delete is Super-Admin-only lifecycle authority (PJL-2 API gate).
  const canDelete = useCanPerformLifecycleDelete();

  // PJL-3 — Active | Archived only. Song History lives on the sidebar route
  // `/uploader-history` (owner: do not reintroduce a History tab here).
  const [view, setView] = useState<JobsView>("active");

  const [filters, setFilters] = useState<FilterState>(() => ({
    ...EMPTY_FILTERS,
    ...(searchParams.get("lineageTrackId")
      ? { lineageTrackId: searchParams.get("lineageTrackId") || undefined }
      : {}),
  }));
  const [uploaderInput, setUploaderInput] = useState("");
  // PJL-3.1 — free-text search draft for the typeable Select (applied on
  // Enter / option pick / clear).
  const [searchInput, setSearchInput] = useState("");
  const [resetCounter, setResetCounter] = useState(0);
  // Dense page seek when jump is supported; otherwise opaque pageToken stack.
  const [listPage, setListPage] = useState(1);
  const [tokenStack, setTokenStack] = useState<(string | null)[]>([null]);

  const currentToken = tokenStack[tokenStack.length - 1];

  const hasPrimaryFilter = !!(
    filters.state ||
    filters.decision ||
    filters.failedCheckpoint ||
    filters.needsReview ||
    filters.uploaderSub ||
    filters.lineageTrackId
  );
  // Page jump uses the materializer dense index — unavailable for search,
  // date windows, and primary filters (legacy cursor path).
  const jumpSupported =
    !filters.search && !filters.fromDate && !filters.toDate && !hasPrimaryFilter;

  const queryArgs: ListProcessingJobsArgs = useMemo(
    () => ({
      ...(filters.state ? { state: filters.state } : {}),
      ...(filters.decision ? { decision: filters.decision } : {}),
      ...(filters.failedCheckpoint
        ? { failedCheckpoint: filters.failedCheckpoint }
        : {}),
      ...(filters.needsReview ? { needsReview: true } : {}),
      ...(filters.uploaderSub ? { uploaderSub: filters.uploaderSub } : {}),
      ...(filters.lineageTrackId
        ? { lineageTrackId: filters.lineageTrackId }
        : {}),
      ...(filters.fromDate ? { fromDate: filters.fromDate } : {}),
      ...(filters.toDate ? { toDate: filters.toDate } : {}),
      ...(filters.search ? { search: filters.search } : {}),
      ...(view === "archived" ? { archived: true } : {}),
      limit: PAGE_SIZE,
      ...(jumpSupported
        ? { page: listPage, pageSize: PAGE_SIZE }
        : currentToken
          ? { pageToken: currentToken }
          : {}),
    }),
    [filters, currentToken, view, jumpSupported, listPage],
  );

  const { data, isLoading, isFetching, isError, error, refetch } =
    useListProcessingJobsQuery(queryArgs, { skip: !canView });

  // PJL-3.2 — server-side totals. Tab counts are unfiltered per view; the
  // matching count carries the current filters + search. All provide the LIST
  // tag, so lifecycle reconciliation refreshes them automatically.
  const countArgs: CountProcessingJobsArgs = useMemo(() => {
    const {
      limit: _limit,
      pageToken: _pageToken,
      page: _page,
      pageSize: _pageSize,
      ...rest
    } = queryArgs;
    return rest;
  }, [queryArgs]);
  const activeCountQuery = useCountProcessingJobsQuery({}, { skip: !canView });
  const archivedCountQuery = useCountProcessingJobsQuery(
    { archived: true },
    { skip: !canView },
  );
  const matchingCountQuery = useCountProcessingJobsQuery(countArgs, {
    skip: !canView,
  });

  // PJL-3.1 — human uploader names (owner UX 2026-07-18). Raw Auth0 subjects
  // never render in this table; the Job Detail page keeps them for debugging.
  const uploaderDirectory = useUploaderDirectory();

  const notConfigured = isError && isProcessingApiNotConfigured(error);
  const hardError = isError && !notConfigured;

  const jobs: ProcessingJobRow[] = data?.jobs ?? [];

  // PJL-3.2 — optimistic removal. Rows the server confirmed archived /
  // restored-away / deleted disappear immediately; skipped or failed rows are
  // never hidden, so they "come back" automatically. A delayed refetch
  // reconciles against the materialized projection.
  const [hiddenJobIds, setHiddenJobIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const visibleJobs = useMemo(
    () => jobs.filter((row) => !hiddenJobIds.has(row.jobId)),
    [jobs, hiddenJobIds],
  );

  // Matching total, adjusted for optimistically hidden rows until the
  // reconcile refetch lands (hidden ids always belong to the current view).
  // count < 0 is the "counts unavailable" sentinel (API predates ?count=true).
  const matchingTotal =
    matchingCountQuery.data != null && matchingCountQuery.data.count >= 0
      ? Math.max(visibleJobs.length, matchingCountQuery.data.count - hiddenJobIds.size)
      : null;
  const matchingExact = matchingCountQuery.data?.exact ?? true;

  // Tab badges: same floor as "Showing X of Y" so a lagging dense counter
  // never under-reports rows already visible on the Active/Archived page.
  const visibleOnActiveTab = view === "active" ? visibleJobs.length : 0;
  const visibleOnArchivedTab = view === "archived" ? visibleJobs.length : 0;
  const activeTabCount = floorLifecycleTabCount(
    activeCountQuery.data?.count,
    visibleOnActiveTab,
  );
  const archivedTabCount = floorLifecycleTabCount(
    archivedCountQuery.data?.count,
    visibleOnArchivedTab,
  );

  // True for the entire duration of ANY bulk run (including single-batch
  // ones with no progress modal). While set, every destructive button —
  // bulk actions, row actions, Clear Batch — is disabled: no second
  // archive/delete/reprocess may start until the current one finishes
  // (owner safety requirement 2026-07-18). Declared before tab catch-up so
  // that effect can suppress lagging Active count refetches mid-BulkOp.
  const [bulkRunning, setBulkRunning] = useState(false);

  // When the page already shows more rows than the counter, refetch so the
  // authoritative badge can catch up. Flooring covers the interim mismatch.
  // Skip while a bulk lifecycle run is in flight — BulkOp archive/restore
  // patches counts before list membership settles; an eager Active refetch
  // against a lagging projection races reconcile and can flash false toasts.
  const tabCountCatchUpKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (bulkRunning) {
      tabCountCatchUpKeyRef.current = null;
      return;
    }
    const serverCount =
      view === "active"
        ? activeCountQuery.data?.count
        : archivedCountQuery.data?.count;
    const visible =
      view === "active" ? visibleOnActiveTab : visibleOnArchivedTab;
    if (!shouldRefetchLifecycleTabCount(serverCount, visible)) {
      tabCountCatchUpKeyRef.current = null;
      return;
    }
    const key = `${view}:${visible}:${serverCount}`;
    if (tabCountCatchUpKeyRef.current === key) return;
    tabCountCatchUpKeyRef.current = key;
    if (view === "active") {
      void activeCountQuery.refetch();
    } else {
      void archivedCountQuery.refetch();
    }
  }, [
    view,
    bulkRunning,
    visibleOnActiveTab,
    visibleOnArchivedTab,
    activeCountQuery.data?.count,
    archivedCountQuery.data?.count,
    activeCountQuery,
    archivedCountQuery,
  ]);

  const formatTabCount = (
    count: number | undefined,
    exact: boolean | undefined,
  ): string =>
    count == null || count < 0
      ? ""
      : ` (${count.toLocaleString()}${exact === false ? "+" : ""})`;

  // ---- PJL-3.2 selection model -----------------------------------------------
  const [selectedJobIds, setSelectedJobIds] = useState<React.Key[]>([]);
  // Controlled-null like Song Library Batch Actions (never undefined).
  const [batchAction, setBatchAction] = useState<
    "reprocess" | "archive" | "restore" | "delete" | null
  >(null);
  // "Select all matching results" — selection is the whole current filter
  // result set; durable BulkOp runs against the frozen filter snapshot.
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const [bulkResults, setBulkResults] = useState<BulkReprocessResponse | null>(null);
  // Per-row outcomes of the last bulk run with skips/failures. NOT shown
  // automatically — only opened via the completion toast's "View skipped".
  const [lifecycleResults, setLifecycleResults] = useState<{
    title: string;
    results: LifecycleActionResult[];
    failedCount: number;
  } | null>(null);

  const clearSelection = () => {
    setSelectedJobIds([]);
    setSelectAllMatching(false);
    setBatchAction(null);
  };

  const clearSelectionBecauseFiltersChanged = () => {
    const had =
      selectAllMatching || selectedJobIds.length > 0;
    clearSelection();
    if (had) {
      toast("Selection cleared because filters changed.");
    }
  };

  // Selected rows resolved back to their data (for state-aware action counts).
  const selectedRows = useMemo(
    () => visibleJobs.filter((row) => selectedJobIds.includes(row.jobId)),
    [visibleJobs, selectedJobIds],
  );

  // Governed jobs (living Song) cannot be archived from Song Processing.
  // Stalled in-flight standalone jobs ARE archivable (Operator Guidance).
  const rowIsArchivable = (row: ProcessingJobRow) =>
    !isGovernedJob(row) &&
    (ARCHIVABLE_STATES.has(row.state) || isJobStalled(row));

  // Active-view checkboxes allow every archivable job (superset of
  // reprocessable) — Reprocess acts on the reprocessable subset only.
  const reprocessableSelectedIds = useMemo(
    () =>
      selectedRows
        .filter((row) => REPROCESSABLE_STATES.has(row.state))
        .map((row) => row.jobId),
    [selectedRows],
  );
  // Archive Selected only targets standalone archivable jobs (governed blocked).
  const archivableSelectedIds = useMemo(
    () => selectedRows.filter(rowIsArchivable).map((row) => row.jobId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRows],
  );

  // Active: selectable if reprocessable OR standalone-archivable so both
  // bulk actions can share the checkbox column.
  const selectableRowCount = useMemo(
    () =>
      view === "archived"
        ? visibleJobs.length
        : visibleJobs.filter(
            (row) =>
              rowIsArchivable(row) || REPROCESSABLE_STATES.has(row.state),
          ).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleJobs, view],
  );
  // Offer query-wide upgrade when the whole page is selected. Include the
  // single-page case (matchingTotal === visibleJobs.length) so Archive All
  // does not silently collapse to an eligible-ids subset.
  const offerSelectAllMatching =
    !selectAllMatching &&
    selectableRowCount > 0 &&
    selectedJobIds.length >= selectableRowCount &&
    matchingTotal != null &&
    matchingTotal >= visibleJobs.length &&
    matchingTotal > 0;

  /** Entire current result set is in scope (banner or full single-page select). */
  const isQueryWideSelection =
    selectAllMatching ||
    (matchingTotal != null &&
      matchingTotal > 0 &&
      selectedJobIds.length >= matchingTotal);

  /** The count shown on bulk buttons/modals: whole matching set or checked rows. */
  const effectiveSelectionCount = isQueryWideSelection
    ? matchingTotal ?? selectedJobIds.length
    : selectedJobIds.length;

  // Optimistic Active/Archived count + list-membership patches + generation-
  // guarded reconcile (same class of fix as Song Library). Never blind-
  // invalidate LIST alone — a lagging projection can omit one of N confirmed
  // jobs (e.g. two "3AM Somewhere" + House Tour → Archived shows only 2).
  const scheduleReconcile = (
    op: ProcessingLifecycleCountOp,
    succeededCount: number,
    expectedJobIds: string[] = [],
  ) => {
    if (succeededCount > 0) {
      patchProcessingJobsCounts(dispatch, op, succeededCount);
    }
    scheduleProcessingJobsListReconcile(dispatch, {
      delayMs: RECONCILE_DELAY_MS,
      op,
      expectedJobIds,
      onSettled: () => {
        setHiddenJobIds(new Set());
      },
      onReconcileFailed: () => {
        toast.error(
          "Song Processing lists could not be fully reconciled with the server. Showing the best-known Active/Archived membership — refresh if a job is missing from both tabs.",
          { duration: 8000 },
        );
      },
    });
  };

  /** Holds the progress bar at 100% briefly before it closes, so the run
   * visibly completes instead of the dialog vanishing mid-bar (owner #5). */
  const FINISH_HOLD_MS = 700;
  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  /** Splits ids into API-cap-sized batches (the 25-id boundary is kept for
   * legacy page-only bulk endpoints). */
  const chunkIds = (ids: string[]): string[][] => {
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += MAX_BULK_LIFECYCLE) {
      chunks.push(ids.slice(i, i + MAX_BULK_LIFECYCLE));
    }
    return chunks;
  };

  const buildBulkOpFilter = (): CreateProcessingBulkOpRequest["filter"] => ({
    view,
    ...(filters.state ? { state: filters.state } : {}),
    ...(filters.decision ? { decision: filters.decision } : {}),
    ...(filters.needsReview ? { needsReview: true } : {}),
    ...(filters.failedCheckpoint
      ? { failedCheckpoint: filters.failedCheckpoint }
      : {}),
    ...(filters.uploaderSub ? { uploaderSub: filters.uploaderSub } : {}),
    ...(filters.lineageTrackId
      ? { lineageTrackId: filters.lineageTrackId }
      : {}),
    ...(filters.fromDate ? { fromDate: filters.fromDate } : {}),
    ...(filters.toDate ? { toDate: filters.toDate } : {}),
    ...(filters.search ? { search: filters.search } : {}),
  });

  const pollBulkOpUntilDone = async (
    bulkOpId: string,
    verb: BulkRunProgress["verb"],
  ): Promise<ProcessingBulkOpStatus> => {
    // Shell host owns the progress modal + keeps it alive across routes.
    const done = waitForActiveBulkOpTerminal(bulkOpId);
    startActiveBulkOp(bulkOpId, verb);
    return done;
  };

  const [bulkArchive, bulkArchiveState] = useBulkArchiveProcessingJobsMutation();
  const [bulkRestore, bulkRestoreState] = useBulkRestoreProcessingJobsMutation();
  const [bulkDelete, bulkDeleteState] = useBulkDeleteProcessingJobsMutation();
  const [createBulkOp] = useCreateProcessingBulkOpMutation();
  const [applyBulkOp] = useApplyProcessingBulkOpMutation();
  const [deletionScope, setDeletionScope] =
    useState<DestructiveDeleteDryRunSummary | null>(null);
  const [preparingDeletionScope, setPreparingDeletionScope] = useState(false);
  const stageEnvName = getAppStage();

  const submitBulkReprocess = async () => {
    setBulkError("");
    setBulkRunning(true);
    const ids = reprocessableSelectedIds;
    try {
      // Durable BulkOp owns discovery/batching/retry — never client-chunk
      // the legacy ≤25 reprocess endpoint as a work queue.
      const body: CreateProcessingBulkOpRequest = {
        action: "reprocess",
        idempotencyKey:
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `bulk-reprocess-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        selectionMode: "ids",
        filter: buildBulkOpFilter(),
        ids,
      };
      const created = await createBulkOp(body).unwrap();
      const status = await pollBulkOpUntilDone(created.bulkOpId, "Reprocessing");
      const c = status.counters || {
        examined: 0,
        succeeded: 0,
        skipped: 0,
        conflicts: 0,
        failed: 0,
      };
      await sleep(FINISH_HOLD_MS);
      setBulkConfirmOpen(false);
      clearSelection();
      const recent = status.recentItems || [];
      setBulkResults({
        requested: c.succeeded,
        rejected: c.skipped + c.conflicts + c.failed,
        results: recent.map((item) => ({
          sourceJobId: item.jobId,
          ok: item.status === "succeeded",
          message: item.message || undefined,
        })),
      });
      if (c.failed === 0 && c.skipped + c.conflicts === 0) {
        toast.success(
          `Started ${c.succeeded.toLocaleString()} reprocess job${c.succeeded === 1 ? "" : "s"}.`,
        );
      } else {
        toast.success(
          `Reprocess finished: ${c.succeeded.toLocaleString()} started, ${(c.skipped + c.conflicts).toLocaleString()} skipped, ${c.failed.toLocaleString()} failed.`,
        );
      }
    } catch (err) {
      setBulkError(extractProcessingErrorMessage(err));
    } finally {
      setBulkRunning(false);
    }
  };

  // ---- PJL-3 archive / restore / delete ---------------------------------------
  // createBulkOp / archive mutations declared above (shared with reprocess BulkOp).

  // Which lifecycle modal is open, and for which job ids (row action → one id;
  // header bulk action → the whole selection). selectAll runs as a durable
  // BulkOp against the frozen filter snapshot (no client ID expansion).
  const [lifecycleModal, setLifecycleModal] = useState<
    | {
        kind: "archive" | "restore" | "delete";
        jobIds: string[];
        selectAll?: boolean;
      }
    | null
  >(null);
  const [lifecycleError, setLifecycleError] = useState("");

  const lifecycleBusy =
    bulkArchiveState.isLoading ||
    bulkRestoreState.isLoading ||
    bulkDeleteState.isLoading;

  const openLifecycleModal = (
    kind: "archive" | "restore" | "delete",
    jobIds: string[],
    selectAll = false,
  ) => {
    setLifecycleError("");
    setLifecycleModal({ kind, jobIds, selectAll });
  };

  // How many jobs targeted by the open archive modal are STALLED in-flight
  // jobs (terminalized with stalled-archived-by-operator — the modal explains).
  const archiveModalStalled = useMemo(() => {
    if (lifecycleModal?.kind !== "archive") return 0;
    const targets = new Set(lifecycleModal.jobIds);
    return jobs.filter((row) => targets.has(row.jobId) && isJobStalled(row)).length;
  }, [lifecycleModal, jobs]);

  // How many jobs targeted by the open archive modal are parked awaiting
  // review (they get terminalized to not_approved — the modal calls it out).
  const archiveModalAwaitingReview = useMemo(() => {
    if (lifecycleModal?.kind !== "archive") return 0;
    const targets = new Set(lifecycleModal.jobIds);
    return jobs.filter(
      (row) => targets.has(row.jobId) && row.state === "awaiting_review",
    ).length;
  }, [lifecycleModal, jobs]);

  const finalizeBulkOpLifecycle = async (
    kind: "archive" | "restore" | "delete",
    verbPast: "Archived" | "Restored" | "Deleted",
    status: ProcessingBulkOpStatus,
    submittedIds: string[] = [],
  ) => {
    const c = status.counters || {
      examined: 0,
      succeeded: 0,
      skipped: 0,
      conflicts: 0,
      failed: 0,
    };
    clearSelection();
    if (c.succeeded > 0) {
      // recentItems is a truncated sample (~50). On clean full success, fall
      // back to the submitted id set so Active→Archived membership patches
      // cover every confirmed job (same pattern as Song Library PD BulkOp).
      const okIds = resolveBulkOpSucceededJobIds({
        succeededCount: c.succeeded,
        failedCount: c.failed,
        skippedCount: c.skipped + c.conflicts,
        totalMatched: status.totalMatched,
        recentItems: status.recentItems,
        submittedIds,
      });
      if (okIds.length > 0) {
        const knownRows = new Map(
          jobs
            .filter((row) => okIds.includes(row.jobId))
            .map((row) => [row.jobId, row] as const),
        );
        patchProcessingJobsLists(dispatch, okIds, kind, knownRows);
        if (kind === "archive" || kind === "restore") {
          setHiddenJobIds((prev) => new Set([...Array.from(prev), ...okIds]));
        }
      }
      scheduleReconcile(kind, c.succeeded, okIds);
    }
    const skipped = c.skipped + c.conflicts;
    const hasDetail = skipped > 0 || c.failed > 0;
    const resultsFromItems: LifecycleActionResult[] = (
      status.recentItems || []
    ).map((item) => ({
      jobId: item.jobId,
      ok: item.status === "succeeded",
      message: item.message || undefined,
      reasonCode:
        item.status === "skipped" || item.status === "conflict"
          ? item.reasonCode || "other"
          : undefined,
    }));
    // When recentItems is a truncated sample, synthesize skip tallies so the
    // toast still phrases belongs-to-active-song from counters.
    if (
      resultsFromItems.filter((r) => !r.ok).length < skipped &&
      skipped > 0
    ) {
      const knownSkips = resultsFromItems.filter((r) => !r.ok).length;
      for (let i = knownSkips; i < skipped; i += 1) {
        resultsFromItems.push({
          jobId: `skipped-${i}`,
          ok: false,
          reasonCode: "belongs-to-active-song",
          message:
            "This Processing Job belongs to an active song. Manage or archive the song from Song Library.",
        });
      }
    }
    const resultsTitle = `${verbPast} ${c.succeeded.toLocaleString()} of ${Math.max(status.totalMatched, c.examined).toLocaleString()} job${status.totalMatched === 1 ? "" : "s"}`;
    const completion = buildLifecycleCompletionMessage(
      verbPast,
      [
        ...Array.from({ length: c.succeeded }, (_, i) => ({
          jobId: `ok-${i}`,
          ok: true,
        })),
        ...resultsFromItems.filter((r) => !r.ok),
      ],
      c.failed,
      hasDetail && resultsFromItems.some((r) => !r.ok)
        ? () => {
            setLifecycleResults({
              title: resultsTitle,
              results: resultsFromItems.filter((r) => !r.ok || r.message),
              failedCount: c.failed,
            });
          }
        : undefined,
    );
    const show = completion.tone === "error" ? toast.error : toast.success;
    show(completion.node, { duration: c.failed > 0 ? 9000 : 7000 });
  };

  // P0 delete: ≤25 uses sync path with shared operationId; >25 / select-all
  // prepare a frozen deletion scope (internal preflight), then elevated apply.
  // No user-facing Dry Run Preview lifecycle step.
  const prepareDeletionScope = async (reason: string) => {
    if (!lifecycleModal || lifecycleModal.kind !== "delete") return;
    const { jobIds, selectAll } = lifecycleModal;
    setLifecycleError("");
    setPreparingDeletionScope(true);
    try {
      const body: CreateProcessingBulkOpRequest = {
        action: "delete",
        idempotencyKey:
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `bulk-del-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        selectionMode: selectAll ? "allMatching" : "ids",
        filter: buildBulkOpFilter(),
        ...(selectAll ? {} : { ids: jobIds }),
        confirm: "DELETE",
        reason,
      };
      const created = await createBulkOp(body).unwrap();
      if (!created.dryRun) {
        throw new Error("Deletion scope was not returned for this delete operation.");
      }
      setDeletionScope(created.dryRun);
    } catch (err) {
      setLifecycleError(extractProcessingErrorMessage(err));
    } finally {
      setPreparingDeletionScope(false);
    }
  };

  const applyElevatedDelete = async (payload: ElevatedDeleteConfirm) => {
    if (!deletionScope) return;
    setLifecycleError("");
    setBulkRunning(true);
    const bulkOpId = deletionScope.operationId;
    const eligible = Number(
      deletionScope.approvedEligibleCount ??
        deletionScope.totals?.eligibleStandalone ??
        0,
    );
    const scopeSnapshot = deletionScope;
    try {
      setLifecycleModal(null);
      setDeletionScope(null);
      // Operator only types DELETE + reason. Phrase / env / count are taken
      // from the frozen scope so apply works against the current staging API
      // (and remains harmless once the API drops those gates).
      await applyBulkOp({
        bulkOpId,
        confirm: "DELETE",
        reason: payload.reason,
        environment: String(scopeSnapshot.stage || stageEnvName),
        approvedCount: eligible,
        confirmPhrase: scopeSnapshot.confirmPhrase || undefined,
      }).unwrap();
      const status = await pollBulkOpUntilDone(bulkOpId, "Deleting");
      await finalizeBulkOpLifecycle(
        "delete",
        "Deleted",
        status,
        // Elevated delete may be query-wide; prefer scope sample when present.
        Array.isArray(scopeSnapshot.sampleEligibleIds)
          ? scopeSnapshot.sampleEligibleIds
          : [],
      );
      // Failures / auto-stop: surface the server stop reason (finalize toast
      // already covers the normal "N jobs deleted" success path).
      if (status.status === "auto_stopped" || status.status === "failed") {
        toast.error(
          status.errorMessage ||
            `Delete stopped early (${status.status}). Check failed rows and retry.`,
          { duration: 12000 },
        );
      }
    } catch (err) {
      const message =
        extractProcessingErrorMessage(err) || "Permanent delete failed.";
      setLifecycleError(message);
      toast.error(message, { duration: 10000 });
    } finally {
      setBulkRunning(false);
    }
  };

  const submitLifecycleAction = async (reason: string) => {
    if (!lifecycleModal) return;
    const { kind, jobIds, selectAll } = lifecycleModal;
    setLifecycleError("");
    const verb =
      kind === "archive" ? "Archiving" : kind === "restore" ? "Restoring" : "Deleting";
    const verbPast =
      kind === "archive" ? "Archived" : kind === "restore" ? "Restored" : "Deleted";
    const deleteCount = selectAll ? matchingTotal ?? jobIds.length : jobIds.length;
    const deleteNeedsElevated =
      kind === "delete" && (!!selectAll || deleteCount > NORMAL_DELETE_MAX);
    if (deleteNeedsElevated) {
      // Elevated path is driven by DeleteJobsModal prepare → confirm.
      await prepareDeletionScope(reason);
      return;
    }
    const useBulkOp =
      kind !== "delete" && (!!selectAll || jobIds.length > 1);
    setBulkRunning(true);
    try {
      if (useBulkOp) {
        setLifecycleModal(null);
        const archiveScope =
          kind === "archive"
            ? archiveBulkOpSelection({
                selectAllMatching: !!selectAll,
                matchingTotal: selectAll ? matchingTotal : null,
                selectedJobIds: selectAll ? [] : jobIds,
              })
            : null;
        const body: CreateProcessingBulkOpRequest = {
          action: kind,
          idempotencyKey:
            typeof crypto !== "undefined" && crypto.randomUUID
              ? crypto.randomUUID()
              : `bulk-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          selectionMode:
            archiveScope?.selectionMode ||
            (selectAll ? "allMatching" : "ids"),
          filter: buildBulkOpFilter(),
          ...(archiveScope
            ? archiveScope.selectionMode === "ids"
              ? { ids: archiveScope.ids }
              : {}
            : selectAll
              ? {}
              : { ids: jobIds }),
          ...(reason ? { reason } : {}),
        };
        const created = await createBulkOp(body).unwrap();
        const status = await pollBulkOpUntilDone(created.bulkOpId, verb);
        await finalizeBulkOpLifecycle(
          kind,
          verbPast,
          status,
          archiveScope?.ids || (selectAll ? [] : jobIds),
        );
        return;
      }

      // Delete ≤25 (or archive/restore single-id path) — direct mutation.
      // Shared operationId prevents cross-chunk bypass of the normal delete cap.
      const ids = jobIds;
      const batches = chunkIds(ids);
      const results: LifecycleActionResult[] = [];
      const tally = { completed: 0, succeeded: 0, skipped: 0, failed: 0 };
      const allOkIds: string[] = [];
      const knownRows = new Map(
        jobs
          .filter((row) => ids.includes(row.jobId))
          .map((row) => [row.jobId, row] as const),
      );
      const syncDeleteOpId =
        kind === "delete"
          ? typeof crypto !== "undefined" && crypto.randomUUID
            ? `sdel_${crypto.randomUUID().replace(/-/g, "").slice(0, 24)}`
            : `sdel_${Date.now().toString(36)}`
          : null;
      for (let i = 0; i < batches.length; i += 1) {
        const response =
          kind === "archive"
            ? await bulkArchive({
                ids: batches[i],
                ...(reason ? { reason } : {}),
              }).unwrap()
            : kind === "restore"
              ? await bulkRestore({ ids: batches[i] }).unwrap()
              : await bulkDelete({
                  ids: batches[i],
                  reason,
                  ...(syncDeleteOpId ? { operationId: syncDeleteOpId } : {}),
                }).unwrap();
        results.push(...response.results);
        const okIds = response.results
          .filter((row) => row.ok)
          .map((row) => row.jobId);
        tally.completed += response.results.length;
        tally.succeeded += okIds.length;
        tally.skipped += response.results.length - okIds.length;
        if (okIds.length > 0) {
          allOkIds.push(...okIds);
          setHiddenJobIds((prev) => new Set([...Array.from(prev), ...okIds]));
          patchProcessingJobsLists(dispatch, okIds, kind, knownRows);
        }
      }
      setLifecycleModal(null);
      setDeletionScope(null);
      clearSelection();
      if (tally.succeeded > 0) {
        scheduleReconcile(kind, tally.succeeded, allOkIds);
      }
      const hasDetail = tally.skipped > 0 || tally.failed > 0;
      const resultsTitle = `${verbPast} ${tally.succeeded.toLocaleString()} of ${ids.length.toLocaleString()} job${ids.length === 1 ? "" : "s"}`;
      const failedCount = tally.failed;
      const show = failedCount > 0 ? toast.error : toast.success;
      show(
        (t) =>
          buildLifecycleCompletionMessage(
            verbPast,
            results,
            failedCount,
            hasDetail
              ? () => {
                  toast.dismiss(t.id);
                  setLifecycleResults({ title: resultsTitle, results, failedCount });
                }
              : undefined,
          ).node,
        { duration: failedCount > 0 ? 9000 : 7000 },
      );
    } catch (err) {
      if (useBulkOp) {
        toast.error(extractProcessingErrorMessage(err) || "Bulk action failed.");
      } else {
        setLifecycleError(extractProcessingErrorMessage(err));
      }
    } finally {
      setBulkRunning(false);
    }
  };

  const switchView = (next: JobsView) => {
    if (next === view) return;
    setView(next);
    setBatchAction(null);
    setFilters(EMPTY_FILTERS);
    setUploaderInput("");
    setSearchInput("");
    setListPage(1);
    setTokenStack([null]);
    clearSelectionBecauseFiltersChanged();
    // Hidden ids belong to the previous view — e.g. a job archived from
    // Active legitimately appears in Archived, so never carry them across.
    setHiddenJobIds(new Set());
    setResetCounter((prev) => prev + 1);
  };

  const resetPagination = (opts?: { clearSelectionToast?: boolean }) => {
    setListPage(1);
    setTokenStack([null]);
    if (opts?.clearSelectionToast) {
      clearSelectionBecauseFiltersChanged();
    } else {
      clearSelection();
    }
  };

  // PJL-3.2 — if optimistic removals (or a reconcile refetch) empty the
  // current page, step back to the previous valid page instead of showing a
  // blank one. Search-scan continuations (empty page WITH a next token) are
  // excluded — there the operator keeps scanning forward.
  useEffect(() => {
    if (isLoading || isFetching || data == null || visibleJobs.length > 0) {
      return;
    }
    if (jumpSupported) {
      if (listPage > 1) setListPage((p) => Math.max(1, p - 1));
      return;
    }
    if (!data.pageToken && tokenStack.length > 1) {
      setTokenStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
    }
  }, [
    isLoading,
    isFetching,
    data,
    visibleJobs.length,
    tokenStack.length,
    jumpSupported,
    listPage,
  ]);

  const updateFilter = <K extends keyof FilterState>(
    key: K,
    value: FilterState[K],
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    resetPagination({ clearSelectionToast: true });
  };

  const handleUploaderSearch = (value: string) => {
    const trimmed = value.trim();
    updateFilter("uploaderSub", trimmed.length > 0 ? trimmed : undefined);
  };

  // PJL-3.1 — apply the free-text search. Terms under the server minimum
  // clear the search rather than round-tripping a guaranteed 400. Short refs
  // ("SP-0834E", and "TR-52A91" since the server also matches Track ID) are
  // normalized to their canonical-id tail so the server's contains() match
  // finds them; full canonical ids still work.
  const handleJobSearch = (value: string) => {
    const trimmed = normalizeAnyRefSearchTerm(["processingJob", "track"], value);
    const next =
      trimmed.length >= SEARCH_MIN_CHARS ? trimmed : undefined;
    setSearchInput(next ?? "");
    updateFilter("search", next);
  };

  // Album / Song Library style: typeable Select options from the current page
  // plus the live search draft so free-text Enter still works.
  const searchOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { label: string; value: string }[] = [];
    const add = (raw?: string | null) => {
      const text = (raw || "").trim();
      if (!text) return;
      const key = text.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      opts.push({ label: text, value: text });
    };
    add(searchInput);
    add(filters.search);
    for (const job of visibleJobs) {
      add(job.discoveredTitle);
      add(job.discoveredArtist);
      add(job.originalFilename);
      add(job.jobId);
      add(job.trackId);
    }
    return opts.slice(0, 40);
  }, [searchInput, filters.search, visibleJobs]);

  const handleDateRangeChange = (value: unknown) => {
    const range = value as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
    if (!range || (!range[0] && !range[1])) {
      setFilters((prev) => ({ ...prev, fromDate: undefined, toDate: undefined }));
      resetPagination({ clearSelectionToast: true });
      return;
    }
    setFilters((prev) => ({
      ...prev,
      fromDate: range[0] ? range[0].startOf("day").toISOString() : undefined,
      toDate: range[1] ? range[1].endOf("day").toISOString() : undefined,
    }));
    resetPagination({ clearSelectionToast: true });
  };

  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    setUploaderInput("");
    setSearchInput("");
    resetPagination({ clearSelectionToast: true });
    setResetCounter((prev) => prev + 1);
  };

  const activeQuickFilter = quickFilterFromState(filters);

  // Dense index pages support "Page X of Y"; search/date/primary-filter modes
  // stay on opaque cursors (jump input disabled).
  const totalPages =
    matchingTotal != null &&
    matchingExact &&
    jumpSupported
      ? Math.max(1, Math.ceil(matchingTotal / PAGE_SIZE))
      : null;
  const currentListPage = jumpSupported ? listPage : tokenStack.length;
  const paginationLabel =
    totalPages != null
      ? `Page ${currentListPage} of ${totalPages.toLocaleString()}`
      : `Page ${currentListPage}`;

  const applyQuickFilter = (id: QuickFilterId) => {
    setFilters((prev) => ({
      ...EMPTY_FILTERS,
      // Preserve the date range and search; replace the primary filter.
      fromDate: prev.fromDate,
      toDate: prev.toDate,
      search: prev.search,
      ...(id === "needsReview" ? { needsReview: true } : {}),
      ...(id === "approved" ? { decision: "approved" as ProcessingDecision } : {}),
      ...(id === "notApproved" ? { decision: "not_approved" as ProcessingDecision } : {}),
      ...(id === "failed" ? { decision: "failed" as ProcessingDecision } : {}),
    }));
    setUploaderInput("");
    resetPagination({ clearSelectionToast: true });
    setResetCounter((prev) => prev + 1);
  };

  const handleNext = () => {
    if (jumpSupported) {
      setListPage((p) => p + 1);
      clearSelection();
      return;
    }
    const nextToken = data?.pageToken;
    if (!nextToken) return;
    setTokenStack((prev) => [...prev, nextToken]);
    clearSelection();
  };

  const handlePrev = () => {
    if (jumpSupported) {
      setListPage((p) => Math.max(1, p - 1));
      clearSelection();
      return;
    }
    setTokenStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
    clearSelection();
  };

  const handlePageJump = (page: number) => {
    if (!jumpSupported) return;
    setListPage(page);
    setTokenStack([null]);
    clearSelection();
  };

  // Owner fallback: Employee Name → Display Name → Email → raw ID (last
  // resort). Raw fallbacks render muted+mono so they read as IDs, not names.
  const renderUploader = (row: ProcessingJobRow): React.ReactNode => {
    const { label, resolved } = uploaderDirectory.resolve(
      row.actorSub,
      row.actorEmployeeId,
    );
    return resolved ? (
      <span>{label}</span>
    ) : (
      <span className={`${styles.mono} ${styles.muted}`}>{label}</span>
    );
  };

  // Human-friendly short refs for the visible page, collision-safe (owner UX
  // 2026-07-21). Canonical proc_job_ ids stay in URLs, tooltips, and copy.
  const jobRefs = useMemo(() => buildJobRefs(jobs.map((j) => j.jobId)), [jobs]);

  const columns: ColumnType<ProcessingJobRow>[] = [
    {
      // Owner naming 2026-07-21: human-facing "Song Processing ID"
      // (SP-XXXXX); the canonical proc_job_ id stays in the tooltip /
      // Technical Details.
      title: "Song Processing ID",
      dataIndex: "jobId",
      width: 160,
      render: (jobId: string) => (
        <JobRef
          jobId={jobId}
          refLabel={jobRefs.get(jobId)}
          to={`/processing-jobs/${encodeURIComponent(jobId)}`}
          testId="job-ref-link"
        />
      ),
    },
    {
      // KrossKast format: Artist then Title.
      title: "Artist",
      key: "artist",
      width: 140,
      render: (_: unknown, row: ProcessingJobRow) =>
        row.discoveredArtist || <span className={styles.muted}>—</span>,
    },
    {
      // Discovered/identified Title once metadata identification has run;
      // falls back to the original filename for earlier-stage jobs.
      title: "Song Title",
      key: "songTitle",
      width: 220,
      render: (_: unknown, row: ProcessingJobRow) =>
        row.discoveredTitle || row.originalFilename || "—",
    },
    {
      title: "Upload Type",
      dataIndex: "songType",
      width: 130,
      render: (songType: ProcessingJobRow["songType"]) =>
        renderSongTypeBadge(songType),
    },
    {
      // PJL-3.1 (owner 2026-07-18) — ONE overall Status column replaces the
      // redundant Current State + Decision pair. state/decision stay in the
      // data model and on Job Detail for auditing.
      title: "Status",
      key: "status",
      width: 150,
      render: (_: unknown, row: ProcessingJobRow) => renderJobStatusBadge(row),
    },
    {
      // MDL — Song Library is the source of truth; jobs show ownership status.
      title: "Linked Song",
      key: "linkedSong",
      width: 200,
      render: (_: unknown, row: ProcessingJobRow) => {
        const linked = row.linkedSong;
        if (!linked || linked.songStatus === "none") {
          return <span className={styles.muted}>None</span>;
        }
        const statusLabel =
          linked.songStatus === "active"
            ? "Active"
            : linked.songStatus === "archived"
              ? "Archived"
              : linked.songStatus === "deleted"
                ? "Deleted"
                : linked.songStatus === "missing"
                  ? "Missing"
                  : linked.songStatus;
        const label =
          linked.title ||
          (linked.trackId ? formatEntityRef("track", linked.trackId) : "Song");
        return (
          <div>
            <div>
              <Link to={linked.songLibraryPath}>{label}</Link>
            </div>
            <div className={styles.muted} style={{ fontSize: 12 }}>
              {statusLabel}
            </div>
          </div>
        );
      },
    },
    {
      title: "Live Status",
      key: "liveStatus",
      width: 200,
      render: (_: unknown, row: ProcessingJobRow) => {
        const live = livePlaybackDisplay(row);
        return (
          <div>
            <div>{live.label}</div>
            {live.summary ? (
              <div className={styles.muted} style={{ fontSize: 12 }}>
                {live.summary}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Uploader",
      key: "uploader",
      width: 170,
      render: (_: unknown, row: ProcessingJobRow) => renderUploader(row),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      width: 180,
      render: (createdAt: string) => formatDateTime(createdAt),
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      width: 180,
      render: (updatedAt: string) => formatDateTime(updatedAt),
    },
    // Archived view — when/who/why the job was archived.
    ...(view === "archived"
      ? ([
          {
            title: "Archived",
            key: "archivedInfo",
            width: 220,
            render: (_: unknown, row: ProcessingJobRow) => (
              <div>
                <div>{formatDateTime(row.archivedAt ?? undefined)}</div>
                {row.archivedBy ? (
                  <div style={{ fontSize: 12 }}>
                    {/* Human name; raw sub only as a last resort. */}
                    {uploaderDirectory.resolve(row.archivedBy).label}
                  </div>
                ) : null}
                {row.archiveReason ? (
                  <div className={styles.muted} style={{ fontSize: 12 }}>
                    {row.archiveReason}
                  </div>
                ) : null}
              </div>
            ),
          },
        ] as ColumnType<ProcessingJobRow>[])
      : []),
    // PJL-3 / governance row actions — Active: Archive (standalone only) +
    // Open Song (governed). Archived: Restore/Delete.
    // Delete is never rendered from Active.
    ...(canEdit ||
    (view === "archived" && canDelete) ||
    visibleJobs.some((r) => isGovernedJob(r) && r.linkedSong?.songLibraryPath)
      ? ([
          {
            title: "Actions",
            key: "lifecycleActions",
            width: view === "archived" ? 96 : 120,
            render: (_: unknown, row: ProcessingJobRow) => (
              <div className={rowActions.rowActions}>
                {isGovernedJob(row) && row.linkedSong?.songLibraryPath ? (
                  <Link
                    to={row.linkedSong.songLibraryPath}
                    className={rowActions.rowActionBtn}
                    aria-label="Open Song"
                    title="Open Song in Song Library"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      textDecoration: "none",
                    }}
                  >
                    <i className="bi bi-music-note-beamed" aria-hidden="true" />
                  </Link>
                ) : null}
                {view === "active" && canEdit && !isGovernedJob(row) && (
                  <button
                    type="button"
                    className={rowActions.rowActionBtn}
                    disabled={!rowIsArchivable(row) || bulkRunning}
                    aria-label="Archive"
                    title={
                      rowIsArchivable(row)
                        ? isJobStalled(row)
                          ? "Archive — finalize this stalled job as Not Approved (audited)"
                          : "Archive — remove from the Active list (reversible; history kept)"
                        : "Jobs actively processing cannot be archived (stalled jobs can)"
                    }
                    onClick={() => openLifecycleModal("archive", [row.jobId])}
                  >
                    <i className="bi bi-archive" aria-hidden="true" />
                  </button>
                )}
                {view === "archived" && canEdit && (
                  <button
                    type="button"
                    className={rowActions.rowActionBtn}
                    disabled={bulkRunning}
                    aria-label="Restore"
                    title="Restore — return this job to the Active list"
                    onClick={() => openLifecycleModal("restore", [row.jobId])}
                  >
                    <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
                  </button>
                )}
                {view === "archived" && canDelete && (
                  <button
                    type="button"
                    className={`${rowActions.rowActionBtn} ${rowActions.rowActionBtnDanger}`}
                    disabled={bulkRunning}
                    aria-label="Delete"
                    title="Delete — permanently delete this archived job (Super Admin)"
                    onClick={() => openLifecycleModal("delete", [row.jobId])}
                  >
                    <i className="bi bi-trash3" aria-hidden="true" />
                  </button>
                )}
              </div>
            ),
          },
        ] as ColumnType<ProcessingJobRow>[])
      : []),
  ];

  // Batch Actions dropdown — same controlled-null Select pattern as Song
  // Library (antd latches undefined as uncontrolled).
  type ProcessingBatchAction = "reprocess" | "archive" | "restore" | "delete";

  const batchOptions = useMemo(() => {
    if (view === "active") {
      if (!canEdit) return [];
      return [
        {
          label: "Reprocess Selected",
          value: "reprocess" as const,
          disabled:
            !selectAllMatching && reprocessableSelectedIds.length === 0,
        },
        {
          label: "Archive Selected",
          value: "archive" as const,
        },
      ];
    }
    const opts: Array<{
      label: string;
      value: "restore" | "delete";
      disabled?: boolean;
    }> = [];
    if (canEdit) {
      opts.push({ label: "Restore Selected", value: "restore" });
    }
    if (canDelete) {
      opts.push({ label: "Delete Selected", value: "delete" });
    }
    return opts;
  }, [
    view,
    canEdit,
    canDelete,
    selectAllMatching,
    reprocessableSelectedIds.length,
  ]);

  const handleBatchAction = (value: ProcessingBatchAction) => {
    // Always clear so the placeholder returns (matches Song Library delete
    // path — processing modals are the confirmation UX).
    setBatchAction(null);
    if (effectiveSelectionCount === 0 || bulkRunning || lifecycleBusy) return;

    if (value === "reprocess") {
      if (selectAllMatching) {
        toast.error(
          "Reprocess works on selected rows on this page — clear “select all matching” and select the jobs to reprocess.",
          { duration: 7000 },
        );
        return;
      }
      if (reprocessableSelectedIds.length === 0) {
        toast.error(
          "None of the selected jobs can be reprocessed (approved/failed only).",
          { duration: 6000 },
        );
        return;
      }
      setBulkError("");
      setBulkConfirmOpen(true);
      return;
    }

    if (value === "archive") {
      openLifecycleModal(
        "archive",
        isQueryWideSelection ? [] : selectedJobIds.map(String),
        isQueryWideSelection,
      );
      return;
    }

    if (value === "restore") {
      openLifecycleModal(
        "restore",
        selectedJobIds.map(String),
        selectAllMatching,
      );
      return;
    }

    if (value === "delete") {
      openLifecycleModal(
        "delete",
        selectedJobIds.map(String),
        selectAllMatching,
      );
    }
  };

  return (
    <Container fluid>
      <Card style={{ borderRadius: 12 }}>
        <Card.Body>
          <Card.Title style={{ display: "flex", justifyContent: "space-between" }}>
            <PageTitle title="Song Processing" />
            <Button
              onClick={handleReset}
              style={{
                background: "white",
                color: "#333",
                border: "1px solid #e0e0e0",
                borderRadius: "6px",
                padding: "8px 16px",
                fontWeight: "500",
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>

          {/* PJL-3 — Active | Archived only. Song History is the sidebar page. */}
          <SongProcessingLifecycleTabs
            view={view}
            onSelect={switchView}
            activeCountLabel={formatTabCount(
              activeTabCount,
              activeCountQuery.data?.exact,
            )}
            archivedCountLabel={formatTabCount(
              archivedTabCount,
              archivedCountQuery.data?.exact,
            )}
          />

          {filters.lineageTrackId ? (
            <Alert variant="info" className="mb-3" data-testid="lineage-filter-banner">
              Showing jobs for Track{" "}
              <span className={styles.mono}>{filters.lineageTrackId}</span>
              {" · "}
              <button
                type="button"
                className="btn btn-link btn-sm p-0 align-baseline"
                onClick={() => updateFilter("lineageTrackId", undefined)}
              >
                Clear Track filter
              </button>
            </Alert>
          ) : null}

          <Form layout="vertical" className={styles.form}>
            <BootstrapRow className="mb-2">
              <BootstrapCol md={4}>
                <FormItem
                  label="Search"
                  className="customSelectWrapper"
                  layout="vertical"
                >
                  <Select
                    key={`search-reset-${resetCounter}`}
                    showSearch
                    allowClear
                    className={`customSelect ${styles.selectField}`}
                    placeholder="Any title, artist, filename, Job ID, or Track ID"
                    // Controlled-null like Batch Actions / Album Name filters.
                    value={filters.search ?? null}
                    filterOption={false}
                    defaultActiveFirstOption={false}
                    options={searchOptions}
                    notFoundContent={
                      searchInput.trim().length > 0
                        ? "Press Enter to search"
                        : null
                    }
                    onSearch={(value) => setSearchInput(value)}
                    onChange={(value) => {
                      if (value == null || value === "") {
                        handleJobSearch("");
                        return;
                      }
                      handleJobSearch(String(value));
                    }}
                    onInputKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      event.stopPropagation();
                      handleJobSearch(searchInput);
                    }}
                    aria-label={`Search ${view} song processing jobs`}
                  />
                </FormItem>
                {filters.search ? (
                  <p className={styles.searchScopeNote} style={{ marginTop: -12 }}>
                    Showing matches in the{" "}
                    {view === "archived" ? "Archived" : "Active"} tab. Large
                    result sets load in pages — use Next to keep scanning.
                  </p>
                ) : null}
              </BootstrapCol>
              <BootstrapCol md={4}>
                <FormItem
                  label="Status"
                  className="customSelectWrapper"
                  layout="vertical"
                >
                  <Select<QuickFilterId>
                    key={`status-reset-${resetCounter}`}
                    className={`customSelect ${styles.selectField}`}
                    placeholder="Custom filters"
                    // Controlled-null when State/Decision/Uploader combine into
                    // a non-quick-filter primary (same latch rule as Batch Actions).
                    value={activeQuickFilter}
                    onChange={(value) => applyQuickFilter(value ?? "all")}
                    options={QUICK_FILTERS.map((filter) => ({
                      label: filter.label,
                      value: filter.id,
                    }))}
                  />
                </FormItem>
              </BootstrapCol>
              <BootstrapCol md={4}>
                <FormItem label="State" className="customSelectWrapper" layout="vertical">
                  <Select
                    key={`state-reset-${resetCounter}`}
                    allowClear
                    className={`customSelect ${styles.selectField}`}
                    placeholder="Any state"
                    value={filters.state}
                    onChange={(value) => updateFilter("state", value)}
                    options={STATE_OPTIONS}
                  />
                </FormItem>
              </BootstrapCol>
            </BootstrapRow>
            <BootstrapRow className="mb-2">
              <BootstrapCol md={4}>
                <FormItem label="Decision" className="customSelectWrapper" layout="vertical">
                  <Select
                    key={`decision-reset-${resetCounter}`}
                    allowClear
                    className={`customSelect ${styles.selectField}`}
                    placeholder="Any decision"
                    value={filters.decision}
                    onChange={(value) => updateFilter("decision", value)}
                    options={DECISION_OPTIONS}
                  />
                </FormItem>
              </BootstrapCol>
              <BootstrapCol md={4}>
                <FormItem
                  label="Failed Checkpoint"
                  className="customSelectWrapper"
                  layout="vertical"
                >
                  <Select
                    key={`failedCheckpoint-reset-${resetCounter}`}
                    allowClear
                    className={`customSelect ${styles.selectField}`}
                    placeholder="Any failed checkpoint"
                    value={filters.failedCheckpoint}
                    onChange={(value) => updateFilter("failedCheckpoint", value)}
                    options={FAILED_CHECKPOINT_OPTIONS}
                  />
                </FormItem>
              </BootstrapCol>
              <BootstrapCol md={4}>
                {/* Operators pick people by name (owner UX 2026-07-18); the
                    selection still filters server-side by the mapped Auth0
                    subject. Raw-ID entry only appears when the employee
                    directory is unavailable to this viewer. */}
                <FormItem label="Uploader" className="customSelectWrapper" layout="vertical">
                  {uploaderDirectory.options.length > 0 ? (
                    <Select
                      key={`uploader-reset-${resetCounter}`}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      className={`customSelect ${styles.selectField}`}
                      placeholder="Any uploader"
                      value={filters.uploaderSub}
                      onChange={(value) =>
                        updateFilter("uploaderSub", value || undefined)
                      }
                      options={uploaderDirectory.options}
                    />
                  ) : (
                    <Input.Search
                      key={`uploader-reset-${resetCounter}`}
                      allowClear
                      placeholder="Uploader ID"
                      value={uploaderInput}
                      onChange={(event) => setUploaderInput(event.target.value)}
                      onSearch={handleUploaderSearch}
                    />
                  )}
                </FormItem>
              </BootstrapCol>
            </BootstrapRow>
            <BootstrapRow className="mb-2">
              <BootstrapCol md={4}>
                {(canEdit || canDelete) && batchOptions.length > 0 ? (
                  <FormItem
                    label="Batch Actions"
                    className="customSelectWrapper"
                    layout="vertical"
                  >
                    <Select<ProcessingBatchAction>
                      className={`customSelect ${styles.selectField}`}
                      placeholder="Batch Actions"
                      value={batchAction}
                      onChange={handleBatchAction}
                      disabled={
                        effectiveSelectionCount === 0 ||
                        lifecycleBusy ||
                        bulkRunning ||
                        batchOptions.length === 0
                      }
                      options={batchOptions}
                    />
                  </FormItem>
                ) : null}
              </BootstrapCol>
              <BootstrapCol md={8}>
                <FormItem label="Date Range (created/updated)" layout="vertical">
                  <DateRangePicker
                    key={`dateRange-reset-${resetCounter}`}
                    onChange={handleDateRangeChange}
                  />
                </FormItem>
              </BootstrapCol>
            </BootstrapRow>
          </Form>

          {notConfigured ? (
            <Alert variant="info" className="mb-0">
              <Alert.Heading style={{ fontSize: 18 }}>
                Music processing unavailable
              </Alert.Heading>
              <p className="mb-0">{PROCESSING_API_UNAVAILABLE_MESSAGE}</p>
            </Alert>
          ) : hardError ? (
            <Alert variant="danger" className="mb-0" data-testid="song-processing-load-error">
              {(() => {
                const failure = formatSongProcessingLoadFailure(
                  error,
                  extractProcessingErrorMessage,
                );
                return (
                  <>
                    <Alert.Heading style={{ fontSize: 18 }}>
                      {failure.title}
                    </Alert.Heading>
                    <p className="mb-2">{failure.body}</p>
                    {failure.technicalRequestId ? (
                      <p className="small text-muted mb-2">
                        Technical details: request ID{" "}
                        <code>{failure.technicalRequestId}</code>
                      </p>
                    ) : null}
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => {
                        void refetch();
                        void activeCountQuery.refetch();
                        void archivedCountQuery.refetch();
                        void matchingCountQuery.refetch();
                      }}
                      disabled={isFetching}
                    >
                      {isFetching ? "Retrying…" : "Retry"}
                    </Button>
                  </>
                );
              })()}
            </Alert>
          ) : (
            <>
              {/* PJL-3.2 — result count always comes from the server. */}
              {matchingTotal != null && data ? (
                <p className={styles.muted} style={{ marginBottom: 8 }}>
                  Showing {visibleJobs.length.toLocaleString()} of{" "}
                  {matchingTotal.toLocaleString()}
                  {matchingExact ? "" : "+"} matching job
                  {matchingTotal === 1 ? "" : "s"}
                  {view === "archived" ? " (Archived)" : ""}
                </p>
              ) : null}

              {/* PJL-3.2 — two-tier selection: current page vs all matching. */}
              {selectAllMatching && matchingTotal != null ? (
                <Alert
                  variant="info"
                  className="py-2 mb-2 d-flex align-items-center justify-content-between flex-wrap gap-2"
                >
                  <span>
                    All <strong>{matchingTotal.toLocaleString()}</strong>{" "}
                    matching job{matchingTotal === 1 ? "" : "s"}{" "}
                    {matchingTotal === 1 ? "is" : "are"} selected (across all
                    pages).
                  </span>
                  <Button size="sm" variant="outline-dark" onClick={clearSelection}>
                    Clear selection
                  </Button>
                </Alert>
              ) : offerSelectAllMatching ? (
                <Alert
                  variant="light"
                  className="py-2 mb-2 d-flex align-items-center justify-content-between flex-wrap gap-2 border"
                >
                  <span>
                    {selectedJobIds.length.toLocaleString()} job
                    {selectedJobIds.length === 1 ? "" : "s"} selected on this
                    page.
                  </span>
                  <Button
                    size="sm"
                    variant="link"
                    className="p-0"
                    onClick={() => setSelectAllMatching(true)}
                  >
                    Select all {matchingTotal?.toLocaleString()}
                    {matchingExact ? "" : "+"} matching jobs
                  </Button>
                </Alert>
              ) : null}

              <Table
                className={`legacy-row-height-table ${styles.table}`}
                columns={columns}
                dataSource={visibleJobs}
                pagination={false}
                rowKey="jobId"
                bordered
                rowSelection={
                  (view === "active" && canEdit) ||
                  (view === "archived" && (canEdit || canDelete))
                    ? {
                        selectedRowKeys: selectedJobIds,
                        onChange: (keys) => {
                          setSelectedJobIds(keys);
                          // Manually changing checkboxes drops back to
                          // page-level selection — "all matching" must always
                          // be an explicit choice.
                          if (selectAllMatching) setSelectAllMatching(false);
                        },
                        getCheckboxProps: (row: ProcessingJobRow) => ({
                          // Active: standalone archivable only (governed
                          // Archive blocked). Reprocess still uses the
                          // reprocessable subset of selection. Archived: all.
                          disabled:
                            view === "active" &&
                            !rowIsArchivable(row) &&
                            !REPROCESSABLE_STATES.has(row.state),
                        }),
                      }
                    : undefined
                }
                scroll={{ x: true }}
                locale={{
                  emptyText:
                    isLoading || isFetching
                      ? " "
                      : view === "archived"
                        ? "No archived jobs match the current filters."
                        : "No song processing jobs match the current filters.",
                }}
                loading={{
                  spinning: isLoading || isFetching,
                  indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />,
                }}
              />
              <div className="mt-3">
                <PaginationRow
                  page={currentListPage}
                  label={
                    jumpSupported
                      ? undefined
                      : paginationLabel
                  }
                  totalPages={jumpSupported ? totalPages ?? undefined : undefined}
                  onPageChange={jumpSupported ? handlePageJump : undefined}
                  onClickPrevious={handlePrev}
                  handleOnClickNext={handleNext}
                  disableLeft={currentListPage <= 1 || isFetching}
                  disableNext={
                    jumpSupported
                      ? isFetching ||
                        (totalPages != null
                          ? currentListPage >= totalPages
                          : !data?.pageToken)
                      : !data?.pageToken || isFetching
                  }
                />
                {!jumpSupported ? (
                  <p className={`${styles.muted} mt-2 mb-0`} style={{ fontSize: 12 }}>
                    Direct page jump is available on unfiltered Active and
                    Archived views. With search, date range, or a primary
                    filter applied, use Prev/Next only.
                  </p>
                ) : null}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={bulkConfirmOpen}
        onHide={bulkRunning ? undefined : () => setBulkConfirmOpen(false)}
        size="lg"
        scrollable
      >
        <Modal.Header closeButton={!bulkRunning}>
          <Modal.Title>Reprocess {selectedJobIds.length} selected job{selectedJobIds.length === 1 ? "" : "s"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3" data-testid="bulk-reprocess-confirm-copy">
            {(() => {
              const tracklessCount = selectedRows.filter(
                (r) => !r.trackId && !r.lineageTrackId,
              ).length;
              const trackLinkedCount = selectedRows.length - tracklessCount;
              return (
                <>
                  {tracklessCount > 0 ? (
                    <div className="mb-2" data-testid="bulk-reprocess-copy-trackless">
                      <strong>
                        Trackless failed attempts ({tracklessCount}):
                      </strong>
                      <ul className="mb-0 mt-1 ps-3">
                        <li>
                          Each creates a <strong>new Song Processing job</strong>{" "}
                          and re-runs the retained source through the full pipeline.
                        </li>
                        <li>
                          Failed parents remain as Processing / audit history; on
                          successful recovery KrossKast{" "}
                          <strong>automatically archives</strong> the parent.
                        </li>
                        <li>
                          There is <strong>no existing Track ID</strong> to preserve —
                          success creates a Track.
                        </li>
                      </ul>
                    </div>
                  ) : null}
                  {trackLinkedCount > 0 ? (
                    <div data-testid="bulk-reprocess-copy-track-linked">
                      <strong>
                        Track-linked jobs ({trackLinkedCount}):
                      </strong>
                      <ul className="mb-0 mt-1 ps-3">
                        <li>
                          Each creates a <strong>new artifact version (n+1)</strong>{" "}
                          by re-running the full processing pipeline from its
                          retained source file.
                        </li>
                        <li>
                          Prior versions <strong>remain in history</strong> — nothing
                          is overwritten or deleted.
                        </li>
                        <li>
                          Once each new version is approved, the prior active
                          version becomes <strong>superseded</strong>.
                        </li>
                        <li>
                          Each song&apos;s <strong>Track ID remains unchanged</strong>{" "}
                          — full lineage is preserved.
                        </li>
                      </ul>
                    </div>
                  ) : null}
                </>
              );
            })()}
          </Alert>
          <p className="text-muted mb-0">
            Jobs whose source file is no longer retained (production originals are
            deleted after finalize) will be rejected individually and reported below.
          </p>
          {bulkError && (
            <Alert variant="danger" className="mb-0 mt-3">
              {bulkError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setBulkConfirmOpen(false)}
            disabled={bulkRunning}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void submitBulkReprocess()}
            disabled={bulkRunning || selectedJobIds.length === 0}
          >
            {bulkRunning
              ? "Reprocessing…"
              : `Reprocess ${selectedJobIds.length} Job${selectedJobIds.length === 1 ? "" : "s"}`}
          </Button>
        </Modal.Footer>
      </Modal>

      <BulkReprocessResultsModal
        show={bulkResults != null}
        response={bulkResults}
        onClose={() => setBulkResults(null)}
        onOpenJob={(jobId) => {
          setBulkResults(null);
          navigate(`/processing-jobs/${encodeURIComponent(jobId)}`);
        }}
      />

      {/* ---- PJL-3 lifecycle modals ---- */}
      <ArchiveJobsModal
        show={lifecycleModal?.kind === "archive"}
        jobIds={lifecycleModal?.kind === "archive" ? lifecycleModal.jobIds : []}
        jobCount={
          lifecycleModal?.kind === "archive" && lifecycleModal.selectAll
            ? matchingTotal ?? undefined
            : undefined
        }
        awaitingReviewCount={archiveModalAwaitingReview}
        stalledCount={archiveModalStalled}
        busy={lifecycleBusy}
        errorMessage={lifecycleError}
        onCancel={() => setLifecycleModal(null)}
        onConfirm={(reason) => void submitLifecycleAction(reason)}
      />
      <RestoreJobsModal
        show={lifecycleModal?.kind === "restore"}
        jobIds={lifecycleModal?.kind === "restore" ? lifecycleModal.jobIds : []}
        jobCount={
          lifecycleModal?.kind === "restore" && lifecycleModal.selectAll
            ? matchingTotal ?? undefined
            : undefined
        }
        busy={lifecycleBusy}
        errorMessage={lifecycleError}
        onCancel={() => setLifecycleModal(null)}
        onConfirm={() => void submitLifecycleAction("")}
      />
      <DeleteJobsModal
        show={lifecycleModal?.kind === "delete"}
        jobIds={lifecycleModal?.kind === "delete" ? lifecycleModal.jobIds : []}
        jobCount={
          lifecycleModal?.kind === "delete" && lifecycleModal.selectAll
            ? matchingTotal ?? undefined
            : undefined
        }
        busy={lifecycleBusy}
        preparingScope={preparingDeletionScope}
        errorMessage={lifecycleError}
        deletionScope={deletionScope}
        forceElevated={
          lifecycleModal?.kind === "delete" &&
          (!!lifecycleModal.selectAll ||
            (lifecycleModal.selectAll
              ? (matchingTotal ?? 0)
              : lifecycleModal.jobIds.length) > NORMAL_DELETE_MAX)
        }
        environmentName={String(stageEnvName)}
        onCancel={() => {
          setLifecycleModal(null);
          setDeletionScope(null);
          setPreparingDeletionScope(false);
        }}
        onConfirm={(reason) => void submitLifecycleAction(reason)}
        onPrepareScope={(reason) => void prepareDeletionScope(reason)}
        onConfirmElevated={(payload) => void applyElevatedDelete(payload)}
      />
      {/* PJL-3.2 — batch-run progress (25-id batches, sequential). Not
          dismissible while running; completion is a toast (owner #4), never a
          popup that forces another click. */}
      {/* Per-row outcomes — reached ONLY through the completion toast's
          "View skipped" link, never shown automatically. */}
      <BulkLifecycleResultsModal
        show={lifecycleResults != null}
        title={lifecycleResults?.title ?? ""}
        results={lifecycleResults?.results ?? null}
        failedCount={lifecycleResults?.failedCount ?? 0}
        onClose={() => setLifecycleResults(null)}
        onOpenJob={(jobId) => {
          setLifecycleResults(null);
          navigate(`/processing-jobs/${encodeURIComponent(jobId)}`);
        }}
      />
    </Container>
  );
};

export default withAuthenticationRequired(ProcessingJobs);
